<template>
  <div class="min-h-screen bg-bochas-bg px-4 py-6 sm:py-8">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8"
      >
        <h1 class="text-2xl sm:text-3xl font-bold text-white">
          {{ isEdit ? "Editar club" : "Nuevo club" }}
        </h1>
        <router-link
          :to="backTo"
          class="btn-bochas-trans text-sm self-start sm:self-auto"
        >
          ← Volver
        </router-link>
      </div>

      <form @submit.prevent="onSave" class="space-y-6">
        <!-- ============ DATOS BÁSICOS ============ -->
        <div class="card-fbochas p-4 sm:p-6 space-y-4">
          <h2 class="text-lg font-semibold text-white">Datos básicos</h2>

          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2"
              >Nombre *</label
            >
            <input
              v-model="form.name"
              @input="syncGeneratedSlug"
              type="text"
              required
              class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
              placeholder="Ej: Club Atlético Córdoba"
            />
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Slug</label
              >
              <input
                v-model="form.slug"
                type="text"
                readonly
                class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-gray-400 placeholder-gray-500 focus:outline-none cursor-not-allowed"
                placeholder="se-genera-automaticamente"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Asociación *</label
              >
              <SelectField
                :model-value="asociacionSelectValue"
                :options="asociacionOptions"
                placeholder="Seleccioná una asociación"
                :disabled="isAsociacionAdminValue"
                @update:modelValue="onAsociacionChange"
              />
              <p
                v-if="isAsociacionAdminValue"
                class="text-xs text-gray-500 mt-1"
              >
                Tu rol fija la asociación a la que pertenecés.
              </p>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Localidad</label
              >
              <input
                v-model="form.locality"
                @input="syncGeneratedSlug"
                @blur="syncCoordsFromLocation(false, false)"
                type="text"
                class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
                placeholder="Córdoba"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Año de fundación</label
              >
              <input
                v-model="form.foundationYear"
                type="text"
                maxlength="4"
                class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
                placeholder="1935"
              />
            </div>
          </div>
        </div>

        <!-- ============ CONTACTO ============ -->
        <div class="card-fbochas p-4 sm:p-6 space-y-4">
          <h2 class="text-lg font-semibold text-white">Contacto</h2>
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2"
              >Dirección</label
            >
            <input
              v-model="form.address"
              @blur="syncCoordsFromLocation(false, true)"
              type="text"
              class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
              placeholder="Av. Sabattini 1200"
            />
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Teléfono</label
              >
              <input
                v-model="form.phone"
                type="text"
                class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
                placeholder="+54 351 423 8890"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Email</label
              >
              <input
                v-model="form.email"
                type="email"
                class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
                placeholder="contacto@club.com"
              />
            </div>
          </div>
        </div>

        <!-- ============ UBICACIÓN ============ -->
        <div class="card-fbochas p-4 sm:p-6 space-y-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 class="text-lg font-semibold text-white">Ubicación en el mapa</h2>
              <p class="text-xs text-gray-400 mt-1">
                Coordenadas para que el club aparezca en el mapa del Home.
              </p>
            </div>
            <button
              type="button"
              class="btn-bochas-azul text-sm"
              :disabled="locating"
              :class="{ 'opacity-60 cursor-not-allowed': locating }"
              @click="syncCoordsFromLocation(true, true)"
            >
              {{ locating ? "Buscando..." : "Generar coordenadas" }}
            </button>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Latitud</label
              >
              <input
                v-model.number="form.lat"
                type="number"
                step="any"
                class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
                placeholder="-31.4201"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Longitud</label
              >
              <input
                v-model.number="form.lng"
                type="number"
                step="any"
                class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
                placeholder="-64.1888"
              />
            </div>
          </div>
          <p v-if="locationError" class="text-xs text-bochas-rojo">
            {{ locationError }}
          </p>
        </div>

        <!-- ============ DESCRIPCIÓN ============ -->
        <div class="card-fbochas p-4 sm:p-6 space-y-4">
          <h2 class="text-lg font-semibold text-white">Descripción</h2>
          <RichTextEditor
            v-model="form.description"
            placeholder="Una descripción del club, su historia y su actividad."
          />
        </div>

        <!-- ============ LOGO ============ -->
        <div v-if="isEdit" class="card-fbochas p-4 sm:p-6 space-y-4">
          <h2 class="text-lg font-semibold text-white">Logo</h2>
          <div class="flex items-center gap-4">
            <div
              class="w-20 h-20 rounded-full overflow-hidden bg-bochas-bg flex items-center justify-center flex-shrink-0"
            >
              <img
                v-if="form.logoUrl"
                :src="form.logoUrl"
                :alt="form.name"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full bg-degradado-bochas flex items-center justify-center text-white text-xl font-bold"
              >
                {{ initials(form.name) }}
              </div>
            </div>
            <div class="flex-1">
              <input
                ref="logoInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="onLogoChange"
              />
              <button
                type="button"
                :disabled="uploadingLogo"
                @click="logoInput?.click()"
                class="btn-bochas-azul text-sm"
                :class="{ 'opacity-60 cursor-not-allowed': uploadingLogo }"
              >
                {{
                  uploadingLogo
                    ? "Subiendo..."
                    : form.logoUrl
                    ? "Cambiar logo"
                    : "Subir logo"
                }}
              </button>
              <p class="text-xs text-gray-500 mt-1">
                PNG, JPG o WEBP. Se reemplaza el anterior.
              </p>
              <p v-if="uploadErrors.logo" class="text-xs text-bochas-rojo mt-1">
                {{ uploadErrors.logo }}
              </p>
            </div>
          </div>
        </div>

        <!-- ============ HERO ============ -->
        <div v-if="isEdit" class="card-fbochas p-4 sm:p-6 space-y-4">
          <h2 class="text-lg font-semibold text-white">
            Imagen hero (portada)
          </h2>
          <div class="flex items-center gap-4">
            <div
              class="w-32 h-20 rounded-lg overflow-hidden bg-bochas-bg flex items-center justify-center flex-shrink-0"
            >
              <img
                v-if="form.heroImageUrl"
                :src="form.heroImageUrl"
                :alt="form.name"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full bg-degradado-bochas flex items-center justify-center text-xs text-white/60 text-center px-2"
              >
                Sin imagen
              </div>
            </div>
            <div class="flex-1">
              <input
                ref="heroInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="onHeroChange"
              />
              <button
                type="button"
                :disabled="uploadingHero"
                @click="heroInput?.click()"
                class="btn-bochas-azul text-sm"
                :class="{ 'opacity-60 cursor-not-allowed': uploadingHero }"
              >
                {{
                  uploadingHero
                    ? "Subiendo..."
                    : form.heroImageUrl
                    ? "Cambiar hero"
                    : "Subir hero"
                }}
              </button>
              <p class="text-xs text-gray-500 mt-1">
                Imagen panorámica del club.
              </p>
              <p v-if="uploadErrors.hero" class="text-xs text-bochas-rojo mt-1">
                {{ uploadErrors.hero }}
              </p>
            </div>
          </div>
        </div>

        <!-- ============ GALERÍA ============ -->
        <div v-if="isEdit" class="card-fbochas p-4 sm:p-6 space-y-4">
          <h2 class="text-lg font-semibold text-white">Galería de imágenes</h2>
          <p class="text-xs text-gray-400 -mt-2">
            Imágenes adicionales del club (instalaciones, eventos, equipos).
          </p>
          <div
            v-if="gallery.length === 0"
            class="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-700 rounded-lg"
          >
            Aún no hay imágenes en la galería.
          </div>
          <div v-else class="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div
              v-for="(url, idx) in gallery"
              :key="url + idx"
              class="relative group"
            >
              <img
                :src="url"
                :alt="`Galería ${idx + 1}`"
                class="w-full aspect-square object-cover rounded-lg"
              />
              <button
                type="button"
                @click="onRemoveGalleryImage(idx)"
                :disabled="removingGalleryIdx === idx"
                class="absolute top-1 right-1 bg-bochas-rojo text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
              >
                {{ removingGalleryIdx === idx ? "..." : "✕" }}
              </button>
            </div>
          </div>
          <div>
            <input
              ref="galleryInput"
              type="file"
              accept="image/*"
              class="hidden"
              @change="onGalleryAdd"
            />
            <button
              type="button"
              :disabled="uploadingGallery"
              @click="galleryInput?.click()"
              class="btn-bochas-azul text-sm w-full sm:w-auto"
              :class="{ 'opacity-60 cursor-not-allowed': uploadingGallery }"
            >
              {{
                uploadingGallery
                  ? "Subiendo..."
                  : "+ Agregar imagen a la galería"
              }}
            </button>
            <p
              v-if="uploadErrors.gallery"
              class="text-xs text-bochas-rojo mt-1"
            >
              {{ uploadErrors.gallery }}
            </p>
          </div>
        </div>

        <!-- ============ CONTADORES ============ -->
        <div class="card-fbochas p-4 sm:p-6 space-y-4">
          <h2 class="text-lg font-semibold text-white">Contadores</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Cantidad de jugadores</label
              >
              <input
                v-model.number="form.playerCount"
                type="number"
                min="0"
                class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
              />
            </div>
          </div>
        </div>

        <!-- ============ ESTADO ============ -->
        <div class="card-fbochas p-4 sm:p-6 space-y-4">
          <h2 class="text-lg font-semibold text-white">Estado</h2>
          <div class="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="form.status"
                type="radio"
                value="draft"
                class="text-bochas-rojo focus:ring-bochas-rojo"
              />
              <span class="text-gray-300 text-sm"
                >Borrador (no visible en el Home)</span
              >
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="form.status"
                type="radio"
                value="published"
                class="text-bochas-rojo focus:ring-bochas-rojo"
              />
              <span class="text-gray-300 text-sm">Publicado (visible)</span>
            </label>
          </div>
        </div>

        <!-- Error -->
        <div
          v-if="error"
          class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 text-sm rounded-lg px-4 py-3"
        >
          {{ error }}
        </div>

        <!-- Submit -->
        <div class="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            :disabled="
              saving || uploadingLogo || uploadingHero || uploadingGallery
            "
            class="btn-bochas-rojo"
            :class="{
              'opacity-60 cursor-not-allowed':
                saving || uploadingLogo || uploadingHero || uploadingGallery,
            }"
          >
            <span v-if="saving">Guardando...</span>
            <span v-else>{{ isEdit ? "Guardar cambios" : "Crear club" }}</span>
          </button>
          <router-link :to="backTo" class="btn-bochas-trans text-center">
            Cancelar
          </router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import SelectField from "@/components/Common/SelectField.vue";
import RichTextEditor from "@/components/Common/RichTextEditor.vue";
import { getStoredUser } from "@/services/auth/authService";
import { isAsociacionAdmin } from "@/utils/roles";
import {
  crearClub,
  editarClub,
  obtenerClub,
  subirClubLogo,
  subirClubHero,
  agregarImagenGaleria,
  eliminarImagenGaleria,
} from "@/services/clubes/clubesService";
import { listarAsociacionesAdmin } from "@/services/asociaciones/asociacionesService";
import { useConfirm } from "@/composables/useConfirm";

const { confirm } = useConfirm();
const route = useRoute();
const router = useRouter();
const user = ref(getStoredUser());

const isAsociacionAdminValue = computed(() => isAsociacionAdmin(user.value));
const isEdit = computed(() => !!route.params.id);

// "Volver" según el rol:
// - CLUB_ADMIN → /admin/mi-club
// - Otros → /admin/clubes
const backTo = computed(() => {
  if (isAsociacionAdminValue.value || user.value?.role === "BOCHAS_ADMIN") {
    return "/admin/clubes";
  }
  if (user.value?.role === "CLUB_ADMIN") return "/admin/mi-club";
  return "/admin/clubes";
});

const form = reactive({
  name: "",
  slug: "",
  asociacionId: null,
  locality: "",
  foundationYear: "",
  address: "",
  phone: "",
  email: "",
  lat: null,
  lng: null,
  description: "",
  logoUrl: null,
  heroImageUrl: null,
  playerCount: 0,
  status: "published",
});

const asociaciones = ref([]);
const gallery = ref([]);

const saving = ref(false);
const error = ref("");

const logoInput = ref(null);
const heroInput = ref(null);
const galleryInput = ref(null);
const uploadingLogo = ref(false);
const uploadingHero = ref(false);
const uploadingGallery = ref(false);
const removingGalleryIdx = ref(null);
const locating = ref(false);
const locationError = ref("");
const uploadErrors = reactive({ logo: "", hero: "", gallery: "" });

const localityCoords = [
  { name: "Córdoba Capital", lat: -31.4201, lng: -64.1888 },
  { name: "Córdoba", lat: -31.4201, lng: -64.1888 },
  { name: "Río Cuarto", lat: -33.1307, lng: -64.3499 },
  { name: "Villa María", lat: -32.4085, lng: -63.2422 },
  { name: "San Francisco", lat: -31.428, lng: -62.08 },
  { name: "Bell Ville", lat: -32.6325, lng: -62.6884 },
  { name: "Alta Gracia", lat: -31.6719, lng: -64.4272 },
  { name: "Jesús María", lat: -30.9719, lng: -64.0925 },
  { name: "Morteros", lat: -30.8126, lng: -61.99 },
  { name: "Las Varillas", lat: -31.8721, lng: -62.7195 },
  { name: "Luque", lat: -31.65, lng: -63.3833 },
  { name: "Coronel Moldes", lat: -33.6227, lng: -64.5972 },
  { name: "Arroyito", lat: -31.4202, lng: -63.0501 },
  { name: "Oncativo", lat: -31.9135, lng: -63.6825 },
  { name: "Hernando", lat: -32.4263, lng: -63.7333 },
  { name: "Marcos Juárez", lat: -32.6978, lng: -62.1067 },
  { name: "Laboulaye", lat: -34.1266, lng: -63.3912 },
  { name: "Punilla", lat: -31.24, lng: -64.46 },
  { name: "Traslasierra", lat: -31.9459, lng: -65.1896 },
];

const asociacionSelectValue = computed(() =>
  form.asociacionId ? String(form.asociacionId) : "",
);
const asociacionOptions = computed(() => [
  { value: "", label: "Seleccioná una asociación" },
  ...asociaciones.value.map((item) => ({
    value: String(item.id),
    label: item.name,
  })),
]);

function initials(name) {
  if (!name) return "";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function slugify(value) {
  return normalizeText(value)
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function syncGeneratedSlug() {
  form.slug = slugify([form.name, form.locality].filter(Boolean).join(" "));
}

function onAsociacionChange(value) {
  form.asociacionId = value ? Number(value) : null;
  const selected = asociaciones.value.find(
    (asociacion) => Number(asociacion.id) === Number(form.asociacionId),
  );
  if (selected?.locality) {
    form.locality = selected.locality;
    syncGeneratedSlug();
    syncCoordsFromLocation();
  }
}

function syncCoordsFromLocality() {
  const target = normalizeText(form.locality);
  if (!target) return;
  const found = localityCoords.find((item) => {
    const name = normalizeText(item.name);
    return name === target || target.includes(name);
  });
  if (!found) return;
  form.lat = found.lat;
  form.lng = found.lng;
}

async function syncCoordsFromLocation(showError = false, force = false) {
  locationError.value = "";

  if (!force) {
    syncCoordsFromLocality();
    if (form.lat !== null && form.lng !== null) return;
  }

  const query = [form.address, form.locality, "Córdoba", "Argentina"]
    .filter(Boolean)
    .join(", ");

  if (!query) {
    if (showError) locationError.value = "Cargá una dirección o localidad.";
    return;
  }

  locating.value = true;
  try {
    const params = new URLSearchParams({
      format: "json",
      limit: "1",
      q: query,
    });
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?${params.toString()}`,
    );
    const items = await response.json();
    const first = Array.isArray(items) ? items[0] : null;
    if (!first?.lat || !first?.lon) {
      if (showError) locationError.value = "No se encontraron coordenadas.";
      return;
    }
    form.lat = Number(first.lat);
    form.lng = Number(first.lon);
  } catch (_) {
    if (showError) locationError.value = "No se pudieron generar las coordenadas.";
  } finally {
    locating.value = false;
  }
}

watch(
  () => form.locality,
  () => {
    syncGeneratedSlug();
    if (form.lat === null || form.lng === null) syncCoordsFromLocation();
  },
);

async function loadAsociaciones() {
  try {
    const data = await listarAsociacionesAdmin();
    asociaciones.value = data.items || [];
  } catch (err) {
    asociaciones.value = [];
  }
}

async function loadClub() {
  if (!isEdit.value) return;
  try {
    const data = await obtenerClub(route.params.id);
    Object.assign(form, {
      name: data.name || "",
      slug: data.slug || "",
      asociacionId: data.asociacionId || null,
      locality: data.locality || "",
      foundationYear: data.foundationYear || "",
      address: data.address || "",
      phone: data.phone || "",
      email: data.email || "",
      lat: data.lat ?? null,
      lng: data.lng ?? null,
      description: data.description || "",
      logoUrl: data.logoUrl ?? null,
      heroImageUrl: data.heroImageUrl ?? null,
      playerCount: data.playerCount ?? 0,
      status: data.status || "published",
    });
    if (!form.slug) syncGeneratedSlug();
    gallery.value = Array.isArray(data.gallery) ? [...data.gallery] : [];
  } catch (err) {
    error.value =
      err.response?.data?.message ||
      err.message ||
      "No se pudo cargar el club.";
  }
}

async function onLogoChange(e) {
  const file = e.target.files?.[0];
  e.target.value = "";
  if (!file) return;
  uploadErrors.logo = "";
  uploadingLogo.value = true;
  try {
    const res = await subirClubLogo(route.params.id, file);
    form.logoUrl = res.url || res.club?.logoUrl || null;
  } catch (err) {
    uploadErrors.logo =
      err.response?.data?.message || err.message || "No se pudo subir el logo.";
  } finally {
    uploadingLogo.value = false;
  }
}

async function onHeroChange(e) {
  const file = e.target.files?.[0];
  e.target.value = "";
  if (!file) return;
  uploadErrors.hero = "";
  uploadingHero.value = true;
  try {
    const res = await subirClubHero(route.params.id, file);
    form.heroImageUrl = res.url || res.club?.heroImageUrl || null;
  } catch (err) {
    uploadErrors.hero =
      err.response?.data?.message ||
      err.message ||
      "No se pudo subir la imagen hero.";
  } finally {
    uploadingHero.value = false;
  }
}

async function onGalleryAdd(e) {
  const file = e.target.files?.[0];
  e.target.value = "";
  if (!file) return;
  uploadErrors.gallery = "";
  uploadingGallery.value = true;
  try {
    const res = await agregarImagenGaleria(route.params.id, file);
    gallery.value = res.gallery || [...gallery.value, res.url];
  } catch (err) {
    uploadErrors.gallery =
      err.response?.data?.message ||
      err.message ||
      "No se pudo subir la imagen.";
  } finally {
    uploadingGallery.value = false;
  }
}

async function onRemoveGalleryImage(idx) {
  const confirmed = await confirm({
    title: "Eliminar imagen",
    message: "¿Eliminar está imagen de la galería?",
  });
  if (!confirmed) return;
  const url = gallery.value[idx];
  removingGalleryIdx.value = idx;
  try {
    const res = await eliminarImagenGaleria(route.params.id, url);
    gallery.value = res.gallery || gallery.value.filter((_, i) => i !== idx);
  } catch (err) {
    error.value =
      err.response?.data?.message ||
        err.message ||
        "No se pudo eliminar la imagen.";
  } finally {
    removingGalleryIdx.value = null;
  }
}

async function onSave() {
  saving.value = true;
  error.value = "";
  try {
    syncGeneratedSlug();
    if (form.address) await syncCoordsFromLocation(false, true);
    const payload = {
      name: form.name,
      slug: form.slug || null,
      asociacionId: form.asociacionId || null,
      locality: form.locality || null,
      foundationYear: form.foundationYear || null,
      courtType: null,
      address: form.address || null,
      phone: form.phone || null,
      email: form.email || null,
      lat: form.lat ?? null,
      lng: form.lng ?? null,
      description: form.description || null,
      playerCount: Number(form.playerCount) || 0,
      status: form.status,
    };
    if (isEdit.value) {
      await editarClub(route.params.id, payload);
    } else {
      const created = await crearClub(payload);
      // Después de crear, ir a editar para que pueda subir logo/hero/galería.
      router.push(`/admin/clubes/editar/${created.id}`);
      return;
    }
    router.push(backTo.value);
  } catch (err) {
    error.value =
      err.response?.data?.message ||
      err.message ||
      "No se pudo guardar el club.";
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  await loadAsociaciones();
  await loadClub();
  // Si es Asociación_ADMIN, preseleccionar su federación.
  if (
    !isEdit.value &&
    isAsociacionAdminValue.value &&
    user.value?.asociacionId
  ) {
    form.asociacionId = user.value.asociacionId;
  }
  syncGeneratedSlug();
});
</script>
